import React, { useState, ChangeEventHandler } from "react";
import { Tag } from "../../compilers/tag";
import { Category } from "../../compilers/category";
import FilterDropdown from "./dropdown/FilterDropdown";
import "../../stylesheet/components/catalogue/FilterBar.scss";
import "../../stylesheet/components/singlecomponents/FormControl.scss";
import SortByDropdown from "./dropdown/SortByDropdown";
import { useTranslation } from "react-i18next";
import chevron from "../../assets/chevron.svg";
import searchIcon from "../../assets/search.svg";
import { LearningPathway } from "../../compilers/learningPathway";
import Skeleton from "react-loading-skeleton";

type FilterProps = {
  textQuery: string;
  setQueryString: ChangeEventHandler<HTMLInputElement>;
  allCategories: Category[];
  allTags: Tag[];
  activeTags: Tag[];
  addActiveTag: (tag: Tag) => void;
  orderBy: string;
  setOrderBy: (newOrderBy: string) => void;
  setFilterDropdownExpanded: () => void;
  addPriceFilter: (price: string) => void;
  activePriceFilters: string[];
  addDifficultyLevelFilter: (type: string) => void;
  activeDifficultyLevelFilters: string[];
  allLearningPathways: LearningPathway[];
  addLearningPathwayFilter: (type: Tag | LearningPathway) => void;
  activeLearningPathwayFilters: (LearningPathway | Tag)[];
  addSourceFilter: (source: string) => void;
  activeSourceFilters: string[];
  addTypeFilter: (type: string) => void;
  activeTypeFilters: string[];
  section_type: string;
};

const FilterBar: React.FC<FilterProps> = ({
  textQuery,
  setQueryString,
  allCategories,
  allTags,
  activeTags,
  addActiveTag,
  orderBy,
  setOrderBy,
  setFilterDropdownExpanded,
  addPriceFilter,
  activePriceFilters,
  addDifficultyLevelFilter,
  activeDifficultyLevelFilters,
  allLearningPathways,
  addLearningPathwayFilter,
  activeLearningPathwayFilters,
  addSourceFilter,
  activeSourceFilters,
  addTypeFilter,
  activeTypeFilters,
  section_type,
}) => {
  const { t, ready } = useTranslation();
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
  const [showSortByDropdown, setShowSortByDropdown] = useState<boolean>(false);

  const toggleFilterDropdown = () => {
    setFilterDropdownExpanded();
    setShowFilterDropdown(!showFilterDropdown);
  };

  const searchPlaceholder = () => {
    return (
      <img className="filter-search-icon" src={searchIcon} alt="search-icon" />
    );
  };

  const getSortByString = () => {
    if (orderBy === "desc") {
      return t("catalogue-filter.most-recent");
    } else if (orderBy === "asc") {
      return t("catalogue-filter.oldest");
    }
    return t("catalogue-filter.sort");
  };

  const toggleSortByDropdown = () => {
    setShowSortByDropdown(!showSortByDropdown);
  };

  return (
    <>
      <div className="filter-container mt-2">
        <div className="filterDropdown-container">
          <div className="form-control px-0">
            <div
              className="flex-center flex-space-between px-2"
              onClick={toggleFilterDropdown}
            >
              <div>
                {ready ? (
                  `${t("catalogue-filter.filter")}`
                ) : (
                  <Skeleton width={100} />
                )}
              </div>
              <div>
                {showFilterDropdown ? (
                  <img
                    className="filter-chevron up-chevron"
                    src={chevron}
                    alt="Chevron icon"
                  />
                ) : (
                  <img
                    className="filter-chevron "
                    src={chevron}
                    alt="Chevron icon"
                  />
                )}
              </div>
            </div>
            {showFilterDropdown ? (
              <FilterDropdown
                allTags={allTags}
                activeTagFilters={activeTags}
                allCategories={allCategories}
                addActiveTag={addActiveTag}
                addPriceFilter={addPriceFilter}
                activePriceFilters={activePriceFilters}
                addExperienceLevelFilter={addDifficultyLevelFilter}
                activeExperienceLevelFilter={activeDifficultyLevelFilters}
                allLearningPathways={allLearningPathways}
                addLearningPathwayFilter={addLearningPathwayFilter}
                activeLearningPathwaysFilters={activeLearningPathwayFilters}
                addSourceFilter={addSourceFilter}
                activeSourceFilters={activeSourceFilters}
                addTypeFilter={addTypeFilter}
                activeTypeFilters={activeTypeFilters}
                section_type={section_type}
              />
            ) : null}
          </div>
        </div>
        <div className="searchBar-container">
          <div className="searchBar-input-searchIcon">
            <input
              placeholder={ready ? `${t("catalogue-filter.search")}` : ``}
              className="form-control"
              type="text"
              value={textQuery}
              onChange={setQueryString}
            />
            {searchPlaceholder()}
          </div>
        </div>
        <div onClick={toggleSortByDropdown} className="sortby-dropdown">
          <div className="form-control flex-space-between">
            <div>{ready ? getSortByString() : <Skeleton width={80} />}</div>
            <div>
              {showSortByDropdown ? (
                <img
                  className="filter-chevron up-chevron"
                  src={chevron}
                  alt="Chevron icon"
                />
              ) : (
                <img
                  className="filter-chevron "
                  src={chevron}
                  alt="Chevron icon"
                />
              )}
            </div>
          </div>
          {showSortByDropdown ? (
            <SortByDropdown
              setOrderBy={setOrderBy}
              toggleSortByDropdown={toggleSortByDropdown}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default FilterBar;
